import { base64 } from "@scure/base";
import { Transaction } from "@scure/btc-signer";
import { useEffect, useState } from "react";
import { AccountWalletSelector } from "../../account-control";
import { useAppState } from "../../app-state";
import { fetchTemporaryRuneClaim, requestTemporaryRuneClaim, fetchUtxos, filterCardinalUtxos, prepareRuneClaim, submitRuneClaim } from "../../lib/api";
import { transactionExplorerUrl } from "../../lib/explorer-links";
import type { RuneClaimQuote, TemporaryRuneClaimStatus } from "../../lib/rune-claims";
import { keepCardinalUtxos, signPsbtGroups } from "../../lib/wallets";
import "./temporary-rune-claim.css";

function downloadQuote(quote: RuneClaimQuote) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(quote, null, 2)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${quote.claimId}-offline-signing.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function TemporaryRuneClaimPage() {
  const { wallet, authorization, authorizeWallet, setWalletModalOpen } = useAppState();
  const [status, setStatus] = useState<TemporaryRuneClaimStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signedFile, setSignedFile] = useState<File | null>(null);
  const [feeRate, setFeeRate] = useState("2");
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
  useEffect(() => {
    let active = true;
    setStatus(null); setSignedFile(null); setError("");
    if (authorization) {
      fetchTemporaryRuneClaim(authorization).then((value) => { if (active) setStatus(value); })
        .catch((cause: unknown) => { if (active) setError(cause instanceof Error ? cause.message : "Could not check this claim"); });
    }
    return () => { active = false; };
  }, [authorization, wallet]);
  const run = async (action: (token: string) => Promise<void>) => {
    setBusy(true); setError("");
    try {
      const token = await authorizeWallet(true);
      if (!token) throw new Error("Verify your wallet to continue");
      await action(token);
    } catch (cause) { setError(cause instanceof Error ? cause.message : "Claim could not be completed"); }
    finally { setBusy(false); }
  };
  const refresh = () => run(async (token) => setStatus(await fetchTemporaryRuneClaim(token)));
  const claim = () => run(async (token) => {
    if (!wallet) return;
    const entitlement = await requestTemporaryRuneClaim(token);
    const paymentUtxos = await filterCardinalUtxos(await keepCardinalUtxos(wallet, await fetchUtxos(wallet.payment.address)));
    const quote = await prepareRuneClaim(token, entitlement.id, { feeRate: Number(feeRate), idempotencyKey: crypto.randomUUID(), paymentUtxos });
    setSignedFile(null);
    setStatus((previous) => previous ? { ...previous, claim: entitlement, quote } : previous);
  });
  const quote = status?.quote;
  const expired = quote ? quote.expiresAt <= now : false;
  const finished = status?.claim && ["completed", "broadcasting", "reconciling"].includes(status.claim.status);
  const submit = () => run(async (token) => {
    if (!wallet || !quote || !signedFile || expired || !status?.transactionsEnabled) return;
    if (signedFile.size > 500_000) throw new Error("The signed PSBT file is too large");
    // The operator returns base64 text. Merge only signatures into the reviewed transaction.
    const returned = Transaction.fromPSBT(base64.decode((await signedFile.text()).trim()), { allowUnknownOutputs: true });
    const expected = Transaction.fromPSBT(base64.decode(quote.psbtBase64), { allowUnknownOutputs: true });
    if (base64.encode(returned.unsignedTx) !== base64.encode(expected.unsignedTx)) throw new Error("The operator transaction differs from your review");
    if (quote.sourceInputIndexes.some((index) => returned.getInput(index).tapKeySig?.length !== 64)) throw new Error("The offline custody signature is missing");
    expected.combine(returned);
    const signed = await signPsbtGroups(wallet, expected.toPSBT(0), { [wallet.payment.address]: quote.paymentInputIndexes });
    await submitRuneClaim(token, quote.claimId, base64.encode(signed));
    setStatus(await fetchTemporaryRuneClaim(token));
  });
  return <section className="temporary-rune-claim">
    <span className="temporary-rune-eyebrow">SPAM THE CHAIN · ONE-TIME CLAIM</span>
    <h1>100 <span>WORLD•RESOURCE</span></h1>
    <p>Your Rune claim, delivered to your verified Taproot wallet.</p>
    <div className="temporary-rune-panel">
      <AccountWalletSelector label="CLAIM WALLET" />
      {!wallet ? <button className="button primary" onClick={() => setWalletModalOpen(true)}>CONNECT WALLET</button> : <>
        <p className="temporary-rune-address">{wallet.ordinals.address}</p>
        {!status ? <button className="button primary" disabled={busy} onClick={() => void refresh()}>{busy ? "CHECKING…" : "VERIFY ELIGIBILITY"}</button> : <>
          <p role="status">{finished ? (status.claim?.status === "completed" ? "Claim sent. Follow the transaction for confirmation." : "Transaction submitted. Confirmation is being checked.")
            : quote && !expired ? "Eligible · waiting for the operator’s offline signature"
            : status.available ? "Eligible · your 100 Runes are confirmed and ready" : status.inventoryMessage}</p>
          {!finished && (!quote || expired) && <>
            <label>Miner fee (sat/vB)<input type="number" min="0.1" max="20" step="0.1" value={feeRate} onChange={(event) => setFeeRate(event.target.value)} /></label>
            {expired && <p>The signing window expired. Prepare a fresh claim with the operator ready.</p>}
            <button className="button primary" disabled={busy || !status.available} onClick={() => void claim()}>{busy ? "PREPARING…" : "CLAIM 100 RUNES"}</button>
          </>}
          {quote && !expired && !finished && <div className="temporary-rune-signing">
            <p>Network fee: <strong>{quote.networkFeeSats.toLocaleString()} sats</strong> from your payment wallet. Rune postage: 546 sats.</p>
            <p>The operator must sign this exact claim offline. Download the signing package and arrange its return as a base64 PSBT text file. Then approve the fee in your connected wallet.</p>
            <p>Signing window: {Math.max(0, Math.ceil((quote.expiresAt - now) / 60000))} minutes remaining.</p>
            <button className="button" disabled={busy} onClick={() => downloadQuote(quote)}>DOWNLOAD SIGNING PACKAGE</button>
            <label>Operator-signed PSBT<input type="file" accept=".txt,.psbt" disabled={busy} onChange={(event) => setSignedFile(event.target.files?.[0] ?? null)} /></label>
            <button className="button primary" disabled={busy || !signedFile || !status.available || !status.transactionsEnabled} onClick={() => void submit()}>{busy ? "PROCESSING…" : "SIGN FEE & SEND CLAIM"}</button>
          </div>}
          {!status.transactionsEnabled && <p>Staging preview: Bitcoin transaction submission is disabled.</p>}
          {status.claim?.transactionId && <a href={transactionExplorerUrl(status.claim.transactionId)} target="_blank" rel="noreferrer">VIEW TRANSACTION ↗</a>}
          <button className="button" disabled={busy} onClick={() => void refresh()}>REFRESH STATUS</button>
        </>}
      </>}
      {error && <p role="alert" className="temporary-rune-error">{error}</p>}
    </div>
    <p className="temporary-rune-note">Bitcoin mainnet · A wallet signature proves eligibility. Delivery requires the operator’s offline custody signature.</p>
  </section>;
}
